import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

import NavigationMenu from './NavigationMenu'; // Changed from NavigationMenuV2
import NavigationMenuMobile from './NavigationMenuMobile'; // Changed from NavigationMenuMobileV2
import FooterComponent from './FooterComponent';

/**
 * Layout component that wraps all pages and provides proper positioning
 * with respect to the navigation menu
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {Object} props.pageSectionRefsContainer - Refs object for page sections
 * @returns {React.Component} Layout component
 */
const Layout = ({ children, pageSectionRefsContainer }) => {
  const location = useLocation();

  // Determine text color for navigation based on the current page
  const getNavTextColor = () => {
    // Home page has dark background, use white text
    if (location.pathname === '/') {
      return '#ffffff';
    }
    // Other pages have light backgrounds, use dark text
    return '#000000';
  }; // Check if current page should have navigation
  const shouldHideNavigation = location.pathname === '/auth';

  // Check if current page should NOT have footer (pages without footer)
  const shouldHideFooter = () => {
    return (
      location.pathname === '/' ||
      location.pathname === '/auth' ||
      location.pathname === '/contact'
    );
  };

  // Navigation width - increased to 250px
  const navWidth = 225;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Main container that holds content and navigation */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          position: 'relative',
          maxWidth: '100vw', // Ensure it doesn't exceed viewport width
          overflow: 'hidden' // Prevent horizontal scrollbar
        }}
      >
        {/* Content wrapper - this takes the full width */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center', // Center the content
            boxSizing: 'border-box'
          }}
        >
          {/* Content container with max width */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '1125px', // Max content width
              px: {
                // Horizontal padding that decreases at smaller screens
                xs: 2,
                sm: 4,
                md: 5,
                lg: 6,
                xl: 8
              },
              pt: { xs: 3, md: 5 },
              pb: 6,
              boxSizing: 'border-box',
              // Makes sure the right margin provides space for the navigation
              // only on medium screens and up
              mr: {
                xs: 0,
                md: 0 // Right margin equal to nav width on desktop
              }
            }}
          >
            {/* Page content */}
            <Box sx={{ flexGrow: 1 }}>{children}</Box>
          </Box>
        </Box>{' '}
        {/* Navigation overlay - positioned absolutely on the right */}
        {!shouldHideNavigation && (
          <>
            {/* Desktop Navigation Menu V1 */}
            <NavigationMenu
              pageSectionRefsContainer={pageSectionRefsContainer} // Pass the container here
              textColor={getNavTextColor()}
              width={navWidth}
            />

            {/* Mobile Navigation Menu V1 */}
            <NavigationMenuMobile
              pageSectionRefsContainer={pageSectionRefsContainer}
              textColor={getNavTextColor()}
            />
          </>
        )}
      </Box>
      {/* Footer - shown on all pages except Home, Auth, and Contact */}
      {!shouldHideFooter() && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center', // Center the footer content
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '1125px', // Same max content width as main content
              px: {
                // Same horizontal padding as main content
                xs: 2,
                sm: 4,
                md: 5,
                lg: 6,
                xl: 8
              },
              boxSizing: 'border-box',
              mr: {
                xs: 0,
                md: 0 // Same right margin as main content
              }
            }}
          >
            <FooterComponent />
          </Box>
        </Box>
      )}
    </Box>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  pageSectionRefsContainer: PropTypes.object // Changed from refs to pageSectionRefsContainer
};

export default Layout;
